import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Typeahead search for the Spare Parts master. Used in the Job Card
 * drawer and the Spares movement drawer to pick a part_code and
 * autofill description / unit_price / tax_percent in the parent row.
 *
 * Props:
 *   value       — current part_code string (controlled)
 *   onSelect    — (part) => void — called with the full spare_part row on pick
 *   onTypedValue — (string) => void — called on every keystroke; parent should
 *                 update the row's part_code so blur-only workflows still work
 *   testId      — data-testid on the input
 *
 * Behaviour:
 *   * Debounces 200ms and hits `GET /spare-parts?search=` with the raw text.
 *   * Popover shows up to 20 matches (backend caps at ~20k total).
 *   * Enter / click selects. Escape closes.
 */
export default function PartPicker({ value, onSelect, onTypedValue, testId }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hi, setHi] = useState(-1);
  const wrapRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    setQ(value || "");
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const runSearch = (needle) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!needle.trim()) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const { data } = await api.get("/spare-parts", { params: { search: needle.trim() } });
        setResults((data || []).slice(0, 20));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQ(v);
    setHi(-1);
    setOpen(true);
    onTypedValue?.(v);
    runSearch(v);
  };

  const pick = (p) => {
    setQ(p.part_code);
    setOpen(false);
    onSelect(p);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      if (hi >= 0 && results[hi]) { e.preventDefault(); pick(results[hi]); }
    } else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-center bg-canvas border border-canvas-line focus-within:border-ink">
        <input
          value={q}
          onChange={onChange}
          onKeyDown={onKey}
          onFocus={() => q && setOpen(true)}
          data-testid={testId}
          placeholder="part code / desc"
          className="w-full px-1 py-0.5 bg-canvas text-xs font-mono focus:outline-none"
          autoComplete="off"
        />
        <MagnifyingGlass size={10} weight="bold" className="mr-1 text-ink-subtle" />
      </div>
      {open && (results.length > 0 || loading) && (
        <div className="absolute z-50 top-full left-0 mt-0.5 w-[380px] max-h-64 overflow-y-auto bg-canvas-card border border-ink shadow-lg">
          {loading && (
            <div className="px-2 py-2 font-mono text-[10px] text-ink-subtle">Searching…</div>
          )}
          {results.map((p, i) => (
            <button
              key={p.part_code}
              type="button"
              onMouseDown={(e) => e.preventDefault() /* keep focus on input */}
              onClick={() => pick(p)}
              className={`w-full text-left px-2 py-1.5 border-b border-canvas-line last:border-b-0 flex items-center justify-between gap-2 ${
                hi === i ? "bg-canvas-panel" : "hover:bg-canvas-panel/50"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[11px] font-bold">{p.part_code}</div>
                <div className="text-[10px] text-ink-muted truncate">{p.description || "—"}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-[11px] tabular-nums">₹{(p.unit_price || 0).toFixed(2)}</div>
                <div className="text-[9px] font-mono text-ink-subtle">stock {p.current_stock || 0}</div>
              </div>
            </button>
          ))}
          {!loading && results.length === 0 && q.trim() && (
            <div className="px-2 py-2 font-mono text-[10px] text-ink-subtle">No parts match.</div>
          )}
        </div>
      )}
    </div>
  );
}
